import { F } from "pages/AppraisalManagment/constants/legacyApiKeys";

export const scoreFmt = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "";
  return (Math.round(Number(n) * 100) / 100).toFixed(2);
};

/** Always show two decimals for rating / obtained score cells (90 → "90.00"). */
export const formatAppraisalScoreDisplay = (value, empty = "—") => {
  if (value === null || value === undefined || value === "") {
    return empty;
  }
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return String(value);
  }
  return scoreFmt(num);
};

export const normalizeSectionGroupLabel = (groupName = "") =>
  String(groupName)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Backend may include aggregate-only groups that should not appear in section summaries. */
export const isRenderableSectionScoreGroup = (groupName = "") => {
  const normalized = normalizeSectionGroupLabel(groupName).toLowerCase();
  return normalized !== "overall score" && normalized !== "rating summary";
};

const SECTION_SCORE_GROUP_ORDER = {
  "individual objectives": 1,
  "core values": 2,
};

export const compareSectionScoreGroupOrder = (groupA = "", groupB = "") => {
  const normalizedA = normalizeSectionGroupLabel(groupA).toLowerCase();
  const normalizedB = normalizeSectionGroupLabel(groupB).toLowerCase();
  const orderA = SECTION_SCORE_GROUP_ORDER[normalizedA] ?? 99;
  const orderB = SECTION_SCORE_GROUP_ORDER[normalizedB] ?? 99;
  if (orderA !== orderB) return orderA - orderB;
  return normalizedA.localeCompare(normalizedB);
};

export const getRenderableSectionScoreEntries = (sectionScoreBlob = {}) =>
  Object.entries(sectionScoreBlob)
    .filter(([key]) => isRenderableSectionScoreGroup(key))
    .sort(([keyA], [keyB]) => compareSectionScoreGroupOrder(keyA, keyB));

export const APPRAISAL_RATING_SCALE = 10;
/** Monthly appraisal view / reports use a 1–5 overall rating scale. */
export const MONTHLY_APPRAISAL_RATING_SCALE = 5;

export const resolveSectionGroupWeight = (groupData) =>
  Number(
    groupData?.[F.groupWeightSynonym] ??
      groupData?.[F.sectionWeightSynonym] ??
      groupData?.weightage ??
      0
  );

/** @deprecated Use resolveSectionGroupWeight */
export const resolveSectionGroupWeightage = resolveSectionGroupWeight;

export const percentToPointRating = (percent, scale = APPRAISAL_RATING_SCALE) => {
  const value = Number(percent);
  if (!Number.isFinite(value)) return 0;
  const ratingScale = Number(scale) || APPRAISAL_RATING_SCALE;
  return Math.min(ratingScale, (value / 100) * ratingScale);
};

export const formatSectionLabelWithWeight = (label, weightage) => {
  const weight = Number(weightage);
  return `${label}${Number.isFinite(weight) && weight > 0 ? ` (${weight}%)` : ""}`;
};

/** First scoring-table column header from backend section name (label or key). */
export const resolveSectionFirstColumnHeader = (sectionTitle = "") => {
  const normalized = normalizeSectionGroupLabel(sectionTitle).toLowerCase();
  if (normalized === "individual objectives") return "KPI's";
  if (normalized === "core values") return "Core Values";
  return "Question";
};

export const resolveSectionPercentFromMonths = (groupData, normalizedMonths, getMonthScore) => {
  const totalScore = Number(groupData?.total_score);
  if (Number.isFinite(totalScore)) return totalScore;

  if (!normalizedMonths?.length || typeof getMonthScore !== "function") return null;

  const scores = normalizedMonths
    .map((monthKey) => {
      const raw = getMonthScore(groupData, monthKey);
      const num = Number(raw);
      return Number.isFinite(num) ? num : null;
    })
    .filter((value) => value !== null);

  if (!scores.length) return null;
  return scores.reduce((sum, value) => sum + value, 0) / scores.length;
};

export const buildAverageRatingFromSectionBlob = ({
  sectionScoreBlob = {},
  normalizedMonths = [],
  getMonthScore,
}) => {
  const ratingSummary = sectionScoreBlob?.rating_summary;
  if (Array.isArray(ratingSummary?.sections) && ratingSummary.sections.length > 0) {
    const resultsRows = ratingSummary.sections.map((section) => {
      const key = section.key || section.label;
      const baseLabel = normalizeSectionGroupLabel(section.label || section.key);
      const groupData = key ? sectionScoreBlob[key] : null;
      const sectionWeight = Number(
        section.weightage ?? section.weight ?? resolveSectionGroupWeight(groupData)
      );

      return {
        key,
        label: formatSectionLabelWithWeight(baseLabel, sectionWeight),
        avgRating: Number(section.avg_rating) || 0,
        weighted: Number(section.weighted_points) || 0,
      };
    });
    const totalFromSummary = Number(ratingSummary.total_appraisal_rating);
    const totalAppraisal = Number.isFinite(totalFromSummary)
      ? totalFromSummary
      : resultsRows.reduce((sum, row) => sum + row.weighted, 0);

    return { resultsRows, totalAppraisal };
  }

  const sectionEntries = getRenderableSectionScoreEntries(sectionScoreBlob).map(
    ([groupName, groupData]) => {
      const sectionPercent =
        typeof getMonthScore === "function" && normalizedMonths.length
          ? resolveSectionPercentFromMonths(groupData, normalizedMonths, getMonthScore)
          : Number(groupData?.total_score);

      const sectionWeight = resolveSectionGroupWeight(groupData);
      const percent = Number.isFinite(sectionPercent) ? sectionPercent : 0;

      return {
        key: groupName,
        label: normalizeSectionGroupLabel(groupName),
        sectionPercent: percent,
        sectionWeight,
      };
    }
  );

  const resultsRows = sectionEntries.map(({ key, label, sectionPercent, sectionWeight }) => {
    const avgRating = percentToPointRating(sectionPercent);
    const weighted = avgRating * ((Number(sectionWeight) || 0) / 100);
    return {
      key,
      label: formatSectionLabelWithWeight(label, sectionWeight),
      avgRating,
      weighted,
    };
  });

  const totalAppraisal = resultsRows.reduce((sum, row) => sum + row.weighted, 0);

  return { resultsRows, totalAppraisal };
};
